import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Upload, Download, ShieldAlert } from "lucide-react";

function credentialsToCsvBlob(credentials) {
  const header = ["Name", "Email", "Temporary Password"].join(",");
  const rows = credentials.map((c) =>
    [c.name, c.email, c.temporary_password]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(","),
  );
  return new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
}

export default function CSVImport({ courseId, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/courses/${courseId}/students/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      onImported?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadCredentials = () => {
    if (!result?.credentials?.length) return;
    const url = URL.createObjectURL(credentialsToCsvBlob(result.credentials));
    const a = document.createElement("a");
    a.href = url;
    a.download = `roster-credentials-${courseId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="nb-card-static p-5 mb-5">
      <h3 className="font-display text-lg font-bold mb-1">Bulk import students (CSV)</h3>
      <p className="text-xs text-[var(--muted-text)] mb-3">
        Upload a CSV with columns <code className="text-[var(--gold)]">email, name</code>. Each new student gets a unique random temporary password and will be forced to reset it on first login.
      </p>
      <form onSubmit={submit} className="flex flex-wrap gap-2 items-center">
        <input ref={fileRef} type="file" accept=".csv,text/csv" required className="nb-input flex-1 min-w-[240px]" data-testid="csv-file-input" />
        <button data-testid="csv-import-btn" disabled={busy} className="nb-btn nb-btn-yellow flex items-center gap-2">
          <Upload className="w-4 h-4" /> {busy ? "Importing…" : "Import"}
        </button>
      </form>
      {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
      {result && (
        <div data-testid="csv-import-result" className="mt-4 space-y-3 text-sm text-[var(--gold-soft)]">
          <p>
            Created <strong className="text-[var(--gold-bright)]">{result.created_users}</strong> new accounts · enrolled{" "}
            <strong className="text-[var(--gold-bright)]">{result.enrolled}</strong> students.
          </p>
          {result.already_enrolled_skipped > 0 && (
            <p className="text-xs">Skipped {result.already_enrolled_skipped} already-enrolled.</p>
          )}
          {result.credentials?.length > 0 && (
            <div className="border-2 border-[var(--gold)] rounded-lg p-4 bg-[var(--paper-soft)]">
              <div className="flex items-start gap-2 mb-2">
                <ShieldAlert className="w-4 h-4 text-[var(--gold-bright)] shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-[var(--gold-bright)] uppercase tracking-wider">Save these credentials now</p>
                  <p className="text-xs text-[var(--muted-text)] mt-1">
                    These temporary passwords are shown once and never again. Download the CSV and share each row with the matching student privately (email, SMS, or in person).
                  </p>
                </div>
              </div>
              <button
                data-testid="csv-download-credentials"
                onClick={downloadCredentials}
                className="nb-btn nb-btn-yellow text-sm flex items-center gap-2 mt-2"
              >
                <Download className="w-4 h-4" /> Download {result.credentials.length} credential{result.credentials.length === 1 ? "" : "s"} (.csv)
              </button>
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-[var(--gold-soft)] hover:text-[var(--gold)]">Preview</summary>
                <table className="mt-2 w-full text-xs">
                  <thead>
                    <tr className="text-left text-[var(--muted-text)] uppercase">
                      <th className="pb-1 pr-2">Email</th>
                      <th className="pb-1">Temporary password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.credentials.slice(0, 10).map((c) => (
                      <tr key={c.email}>
                        <td className="py-1 pr-2 truncate">{c.email}</td>
                        <td className="py-1 font-mono text-[var(--gold-bright)]">{c.temporary_password}</td>
                      </tr>
                    ))}
                    {result.credentials.length > 10 && (
                      <tr>
                        <td colSpan={2} className="pt-1 text-[var(--muted-text)] italic">
                          …and {result.credentials.length - 10} more. Download the CSV to see all.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </details>
            </div>
          )}
          {result.errors?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer">{result.errors.length} error(s)</summary>
              <ul className="ml-4 list-disc">
                {result.errors.map((e) => (
                  <li key={`${e.row}-${e.value}`}>
                    Row {e.row}: {e.error} ({e.value})
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
